"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.collectRichTextSourcesFromScene = void 0;
exports.isDeclaredAdventureOrCharacterStringVariable = isDeclaredAdventureOrCharacterStringVariable;
exports.isActivityReferenceSelectionAllowedInRichText = isActivityReferenceSelectionAllowedInRichText;
exports.validateActivityReferenceSelectionInFragments = validateActivityReferenceSelectionInFragments;
exports.validateActivityReferenceSelectionInHydratedScene = validateActivityReferenceSelectionInHydratedScene;
exports.getActivityReferenceSelectionConfigInRichText = getActivityReferenceSelectionConfigInRichText;
exports.getActivityReferenceSelectionConfigInHydratedScene = getActivityReferenceSelectionConfigInHydratedScene;
exports.isActivityRedoSelectionAllowedInRichText = isActivityRedoSelectionAllowedInRichText;
exports.validateActivityRedoSelectionInFragments = validateActivityRedoSelectionInFragments;
exports.validateActivityRedoSelectionInHydratedScene = validateActivityRedoSelectionInHydratedScene;
const colorPickerRichText_1 = require("./colorPickerRichText");
Object.defineProperty(exports, "collectRichTextSourcesFromScene", { enumerable: true, get: function () { return colorPickerRichText_1.collectRichTextSourcesFromScene; } });
function isDeclaredAdventureOrCharacterStringVariable(adventure, name) {
    if (!adventure || !name) {
        return false;
    }
    const matches = (decls) => !!decls?.some((d) => d.name === name && d.type === 'string');
    return matches(adventure.variables) || matches(adventure.characterVariables);
}
function getNestedRichTextElements(element) {
    const out = [];
    if (element.type === 'conditional') {
        if (Array.isArray(element.children)) {
            out.push(...element.children);
        }
        if (Array.isArray(element.elseChildren)) {
            out.push(...element.elseChildren);
        }
        return out;
    }
    if (element.type === 'inline-option' && Array.isArray(element.content)) {
        out.push(...element.content);
        return out;
    }
    if (Array.isArray(element.content)) {
        for (const item of element.content) {
            if (item && typeof item === 'object' && 'type' in item) {
                out.push(item);
            }
        }
    }
    return out;
}
function rowAllowsReferenceClick(row, allowLocked) {
    if (allowLocked) {
        return true;
    }
    return row.enabled;
}
/**
 * True if `activityId` is a valid pick for reference mode on the current scene:
 * some `<Activity-List>` embed matches `variableName` + `targetScene`, and the id appears in
 * server-hydrated {@link RichTextElement.activityListRows} with selection rules.
 */
function isActivityReferenceSelectionAllowedInRichText(elements, variableName, targetScene, activityId) {
    const walk = (elts) => {
        for (const el of elts) {
            if (el.type === 'activity-list') {
                const st = el.activityListSelectionTarget;
                const sv = el.activityListSelectionVariable;
                if (st === targetScene && sv === variableName) {
                    const rows = el.activityListRows ?? [];
                    const allowLocked = el.activityListSelectionAllowLocked === true;
                    for (const row of rows) {
                        if (row.activityId === activityId && rowAllowsReferenceClick(row, allowLocked)) {
                            return true;
                        }
                    }
                }
            }
            if (walk(getNestedRichTextElements(el))) {
                return true;
            }
        }
        return false;
    };
    return walk(elements);
}
function validateActivityReferenceSelectionInFragments(fragments, variableName, targetScene, activityId) {
    for (const fragment of fragments) {
        const desc = (0, colorPickerRichText_1.toRichTextDescription)(fragment);
        if (!desc) {
            continue;
        }
        if (isActivityReferenceSelectionAllowedInRichText(desc.elements, variableName, targetScene, activityId)) {
            return true;
        }
    }
    return false;
}
function validateActivityReferenceSelectionInHydratedScene(scene, variableName, targetScene, activityId) {
    return validateActivityReferenceSelectionInFragments((0, colorPickerRichText_1.collectRichTextSourcesFromScene)(scene), variableName, targetScene, activityId);
}
function getActivityReferenceSelectionConfigInRichText(elements, variableName, targetScene, activityId) {
    const walk = (elts) => {
        for (const el of elts) {
            if (el.type === 'activity-list') {
                const st = el.activityListSelectionTarget;
                const sv = el.activityListSelectionVariable;
                if (st === targetScene && sv === variableName) {
                    const rows = el.activityListRows ?? [];
                    const allowLocked = el.activityListSelectionAllowLocked === true;
                    for (const row of rows) {
                        if (row.activityId === activityId && rowAllowsReferenceClick(row, allowLocked)) {
                            const syncCharacterNamesCsv = typeof el.activityListSelectionSyncCharacters === 'string'
                                ? el.activityListSelectionSyncCharacters.trim()
                                : '';
                            return {
                                allowed: true,
                                ...(syncCharacterNamesCsv ? { syncCharacterNamesCsv } : {})
                            };
                        }
                    }
                }
            }
            const nested = walk(getNestedRichTextElements(el));
            if (nested.allowed) {
                return nested;
            }
        }
        return { allowed: false };
    };
    return walk(elements);
}
function getActivityReferenceSelectionConfigInHydratedScene(scene, variableName, targetScene, activityId) {
    for (const fragment of (0, colorPickerRichText_1.collectRichTextSourcesFromScene)(scene)) {
        const desc = (0, colorPickerRichText_1.toRichTextDescription)(fragment);
        if (!desc) {
            continue;
        }
        const config = getActivityReferenceSelectionConfigInRichText(desc.elements, variableName, targetScene, activityId);
        if (config.allowed) {
            return config;
        }
    }
    return { allowed: false };
}
function rowIsCompletedForRedo(row) {
    return row.requirements.some(r => r.key === 'done' && r.met === true);
}
/**
 * True if `activityId` is a valid REDO pick: some `<Activity-List>` embed has
 * {@link RichTextElement.activityListRedoScene} equal to `targetScene`, the id appears in hydrated
 * rows, and that row is completed (`done` requirement met).
 */
function isActivityRedoSelectionAllowedInRichText(elements, targetScene, activityId) {
    const ts = (targetScene ?? '').trim();
    const aid = (activityId ?? '').trim();
    if (!ts || !aid) {
        return false;
    }
    const walk = (elts) => {
        for (const el of elts) {
            if (el.type === 'activity-list') {
                const redoScene = (el.activityListRedoScene || '').trim();
                if (redoScene === ts) {
                    const rows = el.activityListRows ?? [];
                    for (const row of rows) {
                        if (row.activityId === aid && rowIsCompletedForRedo(row)) {
                            return true;
                        }
                    }
                }
            }
            if (walk(getNestedRichTextElements(el))) {
                return true;
            }
        }
        return false;
    };
    return walk(elements);
}
function validateActivityRedoSelectionInFragments(fragments, targetScene, activityId) {
    for (const fragment of fragments) {
        const desc = (0, colorPickerRichText_1.toRichTextDescription)(fragment);
        if (!desc) {
            continue;
        }
        if (isActivityRedoSelectionAllowedInRichText(desc.elements, targetScene, activityId)) {
            return true;
        }
    }
    return false;
}
function validateActivityRedoSelectionInHydratedScene(scene, targetScene, activityId) {
    return validateActivityRedoSelectionInFragments((0, colorPickerRichText_1.collectRichTextSourcesFromScene)(scene), targetScene, activityId);
}
//# sourceMappingURL=activityListReferenceRichText.js.map